import React, { useCallback, useMemo } from "react";

import { IconButton } from "@mui/material";

import usePostDeleteChatBotSession from "@fartherfinance/frontend/api/Chatbot/hooks/useDeleteChatBotSession";
import useGetChatBotMessages from "@fartherfinance/frontend/api/Chatbot/hooks/useGetChatBotMessages";
import usePostCreateChatBotSession from "@fartherfinance/frontend/api/Chatbot/hooks/usePostCreateChatBotSession";
import usePostSendMessage from "@fartherfinance/frontend/api/Chatbot/hooks/usePostSendMessage";
import {
  ChatBotMessage,
  ChatBotSessionId,
} from "@fartherfinance/frontend/api/Chatbot/Types";
import { randomString } from "@fartherfinance/frontend/api/utils/randomString";

import Button from "../../MUI/Button/Button";
import { CHATBOT_EVENTS } from "../constants";
import { useGetIsValidSessionid } from "../hooks/useGetIsValidSessionid";
import { useChatContext, useChatStore } from "../reducer/Context";
import { ChatBotWindowProps } from "../types";
import { ChatAnalyticsProvider } from "../utils/analyticsProvider";
import { generateId } from "../utils/generateId";
import { trackChatbotEvent } from "../utils/trackChatbotEvent";
import AddPlus from "@src/assets/svg/components/AddPlus";
import Arrow from "@src/assets/svg/components/Arrow";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import AnimationContainer from "@src/sharedComponents/AnimationContainer/AnimationContainer";
import Conditional from "@src/sharedComponents/Conditional/Conditional";
import Text from "@src/sharedComponents/Typography/Text";
import { handleApiError } from "@src/utils/handleApiError";

import ChatHeader from "./ChatHeader/ChatHeader";
import ChatHeaderRight from "./ChatHeader/ChatHeaderRight";
import ChatInput from "./ChatInput";
import ChatSessionList from "./ChatSessionList/ChatSessionList";
import MessageList from "./MessageList";

import styles from "../style/ChatBotWindow.module.css";

declare global {
  interface HTMLDivElement {
    scrollTimer?: ReturnType<typeof setTimeout>;
  }

  interface HTMLTextAreaElement {
    scrollTimer?: ReturnType<typeof setTimeout>;
  }
}

const ChatBotWindow: React.FC<ChatBotWindowProps> = ({
  rightPosition,
  isExpanded,
  onToggleExpand,
  hasSessions,
}) => {
  const {
    input,
    sessionId,
    setInput,
    setInput: setDraftInput,
    resetChat,
    setChatSessionId,
    addOptimisticUserMessage,
  } = useChatStore();

  const { state } = useChatContext();

  const advisorAuth = useAdvisorRequestAuth();

  const isValidSessionId = useGetIsValidSessionid(sessionId);

  const { data: chatBotMessagesResponse } = useGetChatBotMessages(
    sessionId,
    isValidSessionId ? advisorAuth : null
  );

  const { mutateAsync: postSendMessage, isLoading: isMutatingSend } =
    usePostSendMessage(advisorAuth);

  const {
    mutateAsync: postCreateChatBotSession,
    isLoading: isMutatingSession,
  } = usePostCreateChatBotSession(advisorAuth);

  const {
    mutateAsync: postDeleteChatBotSession,
    isLoading: isDeletingSession,
  } = usePostDeleteChatBotSession(advisorAuth);

  const isLoading = useMemo(
    () => isMutatingSend || isMutatingSession || isDeletingSession,
    [isMutatingSend, isMutatingSession, isDeletingSession]
  );

  const notify = useStatusNotification();

  const containerStyle = useMemo(
    () =>
      rightPosition !== undefined ? { right: `${rightPosition}px` } : undefined,
    [rightPosition]
  );

  const handleAddChat = useCallback(() => {
    const generateTempId = () =>
      `TEMP_${randomString(4)}-${randomString(4)}-${randomString(4)}`;

    const temporarySessionId = generateTempId() as ChatBotSessionId;

    resetChat();

    setChatSessionId(temporarySessionId);
  }, [resetChat, setChatSessionId]);

  const handleOnClickBack = useCallback(() => {
    resetChat();
    setChatSessionId(null);
  }, [resetChat, setChatSessionId]);

  const handleOnClickAction =
    (action: string) => async (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      if (sessionId === null) {
        return;
      }

      switch (action) {
        case "delete":
          try {
            await postDeleteChatBotSession({ sessionId });

            notify("Chat session deleted", "Success");

            handleOnClickBack();
          } catch (error) {
            handleApiError({
              error,
              errorMessage: "Failed to delete chat session",
              genericErrorMessage:
                "An error occurred while deleting the chat session",
              notify,
            });
          }
          break;
        default:
          console.warn("Unknown action:", action);
      }
    };

  const sendMessageToChatbot = useCallback(
    async ({ sessionId, prompt, messageCount }) => {
      try {
        await postSendMessage({
          body: {
            sessionId,
            prompt,
          },
        });

        addOptimisticUserMessage(null);

        trackChatbotEvent(CHATBOT_EVENTS.SEND_MESSAGE, {
          sessionId,
          messageLength: prompt.length,
        });

        setChatSessionId(sessionId);
      } catch (err) {
        handleApiError({
          error: err,
          errorMessage: "Failed to send message to chatbot",
          genericErrorMessage:
            "An error occurred while sending the message to the chatbot",
          notify,
        });

        trackChatbotEvent(CHATBOT_EVENTS.ERROR, {
          sessionId,
          error: "Failed to send message to chatbot",
          messageCount,
        });
      }
    },
    [postSendMessage, notify, setChatSessionId, addOptimisticUserMessage]
  );

  const handleOnAttemptSendMessage = useCallback(async () => {
    const cleanInput = state.draftInput.trim();

    if (!cleanInput) {
      return;
    }

    const optimisticUserMessageUpdate: ChatBotMessage = {
      messageId: generateId() as ChatBotMessage["messageId"],
      role: "user",
      content: cleanInput,
      timestamp: new Date(),
      status: "pending",
      sessionId: state.sessionId as ChatBotMessage["sessionId"],
    };

    addOptimisticUserMessage(optimisticUserMessageUpdate);

    setDraftInput("");

    if (isValidSessionId) {
      await sendMessageToChatbot({
        sessionId: state.sessionId,
        prompt: cleanInput,
      });
    } else {
      try {
        const response = await postCreateChatBotSession({ body: {} });

        await sendMessageToChatbot({
          sessionId: response.data.sessionId,
          prompt: cleanInput,
        });

        trackChatbotEvent(CHATBOT_EVENTS.FIRST_MESSAGE, {
          sessionId: state.sessionId,
        });

        return;
      } catch (error) {
        handleApiError({
          error,
          errorMessage: "Failed to create chat session",
          genericErrorMessage:
            "An error occurred while creating the chat session",
          notify,
        });
      }
    }
  }, [
    state,
    addOptimisticUserMessage,
    setDraftInput,
    notify,
    postCreateChatBotSession,
    sendMessageToChatbot,
    isValidSessionId,
  ]);

  const chatHeaderTitle = useMemo(() => {
    if (sessionId === null) {
      return "All Chats";
    }

    const sortedMessages = [...(chatBotMessagesResponse?.data || [])].sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    return sortedMessages[0]?.content || "Chat";
  }, [chatBotMessagesResponse, sessionId]);

  const isSessionActive = sessionId !== null;
  const shouldShowSessionList = !isSessionActive && hasSessions;
  const shouldShowMessageView = isSessionActive || !hasSessions;
  const animationKey = sessionId === null ? "all-chats" : "no-session";

  return (
    <ChatAnalyticsProvider>
      <div
        className={toClassName(styles.chatContainer, {
          [styles.chatContainerExpanded]: isExpanded,
        })}
        style={containerStyle}
      >
        <AnimationContainer
          className={styles.chatHeaderAnimationContainer}
          contentKey={animationKey}
        >
          <ChatHeader
            left={
              <Conditional condition={hasSessions}>
                <IconButton
                  onClick={handleOnClickBack}
                  className={styles.controlButton}
                >
                  <Arrow rotate="left" />
                </IconButton>
              </Conditional>
            }
            right={
              <ChatHeaderRight
                isExpanded={isExpanded}
                onToggleExpand={onToggleExpand}
                onAddChat={handleAddChat}
                onAction={handleOnClickAction}
              />
            }
            title={chatHeaderTitle}
          />
        </AnimationContainer>
        <AnimationContainer
          className={styles.chatContent}
          style={isExpanded ? { height: "100%" } : {}}
          contentKey={animationKey}
        >
          <Conditional condition={shouldShowSessionList}>
            <ChatSessionList isExpanded={isExpanded} />
            <div className={styles.newChatButtonContainer}>
              <Button
                variant="contained"
                buttonType="primary"
                text="New Chat"
                onClick={handleAddChat}
                startIcon={<AddPlus fill="var(--iconInverse)" />}
                sx={{ width: "100%" }}
                className={styles.startNewChatButton}
              />
              <Text variant="bodySmallSolid">Internal use only</Text>
            </div>
          </Conditional>

          <Conditional condition={shouldShowMessageView}>
            <MessageList
              isLoading={isLoading}
              isExpanded={Boolean(isExpanded)}
            />
            <ChatInput
              input={input}
              setInput={setInput}
              sendMessage={handleOnAttemptSendMessage}
              isLoading={isLoading}
            />
          </Conditional>
        </AnimationContainer>
      </div>
    </ChatAnalyticsProvider>
  );
};

export default ChatBotWindow;
